import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowRight, BriefcaseBusiness, CheckCircle2, ClipboardList, ExternalLink, Mail } from "lucide-react";
import PageShell from "../components/PageShell";
import TextField from "../components/TextField";
import FormStatus from "../components/FormStatus";
import ShareByEmail from "../components/ShareByEmail";
import JobAlertsForm from "../components/JobAlertsForm";
import useSeo from "../hooks/useSeo";
import { api } from "../lib/api";
import { applicationMethods, jobCategories, jobServiceTypes, jobTypes } from "../data/content";

const initialState = {
  service_type: jobServiceTypes[0],
  company_name: "",
  contact_name: "",
  email: "",
  phone: "",
  company_website: "",
  company_location: "Calgary, Alberta",
  company_description: "",
  company_size: "",
  job_title: "",
  category: jobCategories[0],
  job_location: "",
  job_type: jobTypes[0],
  pay_range: "",
  application_deadline: "",
  job_description: "",
  requirements: "",
  benefits: "",
  apply_method: applicationMethods[0],
  apply_email: "",
  apply_url: "",
  apply_instructions: "",
  notes: "",
};

export default function PostJob() {
  useSeo("Post a Job Free in Calgary", "Companies can submit Calgary job listings for free on TalentBridge for admin review before publication.");
  const [searchParams] = useSearchParams();
  const formRef = useRef(null);
  const [form, setForm] = useState(initialState);
  const [status, setStatus] = useState(null);
  const [errorText, setErrorText] = useState("");
  const [loading, setLoading] = useState(false);

  const onChange = (event) => {
    const { name, value } = event.target;
    if (name === "category" && value === "On-Demand Gigs") {
      setForm({ ...form, category: value, job_type: "On-Demand" });
      return;
    }
    setForm({ ...form, [name]: value });
  };
  const isHiringSupport = form.service_type === "TalentBridge hiring support";
  const isGig = form.category === "On-Demand Gigs";

  const activateGigQuickPost = () => {
    setForm((current) => ({ ...current, service_type: "Free job", category: "On-Demand Gigs", job_type: "On-Demand" }));
    setStatus(null);
    setErrorText("");
    window.requestAnimationFrame(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
  };

  useEffect(() => {
    if (searchParams.get("gig") === "1") {
      activateGigQuickPost();
    }
  }, [searchParams]);

  const submissionPayload = () => {
    const applyMethod = (form.apply_method === "External link" && !form.apply_url.trim()) || (form.apply_method === "Instructions" && form.apply_instructions.trim().length < 10)
      ? "Email"
      : form.apply_method;
    return {
      ...form,
      company_name: form.company_name.trim() || (isGig ? "Gig poster" : "Company not provided"),
      company_location: form.company_location.trim() || "Calgary, Alberta",
      job_title: form.job_title.trim() || (isGig ? "On-Demand Gig" : "Job opportunity"),
      job_location: form.job_location.trim() || form.company_location.trim() || "Calgary, Alberta",
      job_type: form.job_type.trim() || (isGig ? "On-Demand" : "Not specified"),
      pay_range: form.pay_range.trim() || "Not specified",
      requirements: form.requirements.trim() || "See job description.",
      apply_method: applyMethod,
    };
  };

  const submit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setStatus(null);
    setErrorText("");
    try {
      await api.jobPost(submissionPayload());
      setForm(initialState);
      setStatus("success");
    } catch (error) {
      setErrorText(error.response?.data?.detail || "Please check the form and try again.");
      setStatus("error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageShell>
      <section className="bg-white px-5 pb-16 pt-32 md:px-8 lg:pb-24" data-testid="post-job-page-section">
        <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[0.82fr_1.18fr] lg:items-start">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#0066FF]" data-testid="post-job-eyebrow">For companies</p>
            <span className="mt-4 inline-flex bg-emerald-600 px-3 py-1 text-xs font-black uppercase tracking-[0.18em] text-white" data-testid="post-job-path-badge">{isHiringSupport ? "Hiring support" : "Free"}</span>
            <h1 className="mt-5 font-heading text-4xl font-black leading-tight text-[#0A2540] sm:text-5xl lg:text-6xl" data-testid="post-job-title">{isHiringSupport ? "Request Hiring Support" : "Free Job Posting"}</h1>
            <p className="mt-6 text-base leading-8 text-[#475569] md:text-lg" data-testid="post-job-description">
              Post a job for free or request TalentBridge hiring support using a business email. Every listing is reviewed before publication.
            </p>
            <div className="mt-8 grid gap-4">
              <div className="border-l-4 border-[#0066FF] bg-[#F8FAFC] p-5" data-testid="post-job-review-card">
                <ClipboardList className="h-6 w-6 text-[#0066FF]" />
                <p className="mt-3 text-sm font-bold text-[#0A2540]" data-testid="post-job-review-text">Your listing stays pending until TalentBridge approves it for publication.</p>
              </div>
              <div className="border-l-4 border-[#0066FF] bg-[#F8FAFC] p-5" data-testid="post-job-support-card">
                <BriefcaseBusiness className="h-6 w-6 text-[#0066FF]" />
                <p className="mt-3 text-sm font-bold text-[#0A2540]" data-testid="post-job-support-text">Free jobs use your chosen apply method. Hiring-support jobs route applications through TalentBridge for review and shortlisting.</p>
              </div>
              <button type="button" onClick={activateGigQuickPost} className="group border-l-4 border-emerald-600 bg-emerald-50 p-5 text-left transition-transform hover:-translate-y-0.5 hover:bg-emerald-100" data-testid="post-job-quick-gig-button">
                <span className="text-xs font-black uppercase tracking-[0.16em] text-emerald-700" data-testid="post-job-quick-gig-label">Quick post</span>
                <span className="mt-2 flex items-center gap-2 font-heading text-xl font-black text-[#0A2540]" data-testid="post-job-quick-gig-title">Post an On-Demand Gig <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
                <span className="mt-2 block text-sm font-semibold leading-6 text-[#475569]" data-testid="post-job-quick-gig-copy">Jump straight to the lightweight gig form.</span>
              </button>
            </div>
            <div className="mt-6">
              <ShareByEmail
                title="Share employer posting page"
                description="Know another Calgary company hiring? Enter their email and send them this page link."
                path="/post-job"
                subject="Post a job or request hiring support with TalentBridge Consulting"
                body="Hi, I thought you may want to post a job or request hiring support with TalentBridge Consulting. Here is the link:"
                testId="post-job"
              />
            </div>
          </div>

          <form ref={formRef} id="post-job-form" onSubmit={submit} className="grid gap-6 border border-[#E2E8F0] bg-[#FBFBFC] p-6 md:p-8" data-testid="post-job-form">
            <FormStatus status={status} successText="Job post submitted for review." errorText={errorText} />

            <div className="border-b border-[#E2E8F0] pb-2">
              <h2 className="font-heading text-xl font-black text-[#0A2540]" data-testid="post-job-service-section-title">Posting type</h2>
            </div>
            <label className="grid gap-2" data-testid="service_type-field">
              <span className="text-xs font-bold uppercase tracking-[0.16em] text-[#475569]" data-testid="service_type-label">Choose job posting path</span>
              <select name="service_type" value={form.service_type} onChange={onChange} className="min-h-11 border border-[#CBD5E1] bg-white px-3 text-sm text-[#0B1120] focus:outline-none focus:ring-2 focus:ring-[#0066FF]" data-testid="service_type-input">
                {jobServiceTypes.map((type) => <option key={type} value={type}>{type}</option>)}
              </select>
            </label>
            <div className="grid gap-3 border border-[#D8E3F0] bg-white p-4 text-sm text-[#475569] md:grid-cols-2" data-testid="post-job-service-type-help">
              <span className="font-semibold" data-testid="post-job-free-job-help">Free job: candidates apply by email, link, or your instructions.</span>
              <span className="font-semibold" data-testid="post-job-hiring-support-help">Hiring support: TalentBridge collects and reviews applications before sharing shortlisted candidates.</span>
            </div>

            <div className="border-b border-[#E2E8F0] pb-2">
              <h2 className="font-heading text-xl font-black text-[#0A2540]" data-testid="post-job-company-section-title">Company profile</h2>
            </div>
            <div className="grid gap-5 md:grid-cols-2">
              <TextField id="company_name" label="Company name (optional)" value={form.company_name} onChange={onChange} required={false} />
              <TextField id="contact_name" label="Contact name" value={form.contact_name} onChange={onChange} />
              <TextField id="email" label="Business email" type="email" value={form.email} onChange={onChange} placeholder="name@company.ca" />
              <TextField id="phone" label="Phone" value={form.phone} onChange={onChange} />
              <TextField id="company_website" label="Company website" value={form.company_website} onChange={onChange} required={false} />
              <TextField id="company_location" label="Company location (optional)" value={form.company_location} onChange={onChange} required={false} />
              <TextField id="company_size" label="Company size" value={form.company_size} onChange={onChange} required={false} placeholder="Example: 11–50 employees" />
            </div>
            <TextField id="company_description" label="Company description" multiline value={form.company_description} onChange={onChange} required={false} placeholder="A short employer profile candidates can review." />

            <div className="border-b border-[#E2E8F0] pb-2 pt-2">
              <h2 className="font-heading text-xl font-black text-[#0A2540]" data-testid="post-job-details-section-title">Job details</h2>
            </div>
            {isGig && (
              <div className="border-l-4 border-emerald-600 bg-emerald-50 p-4 text-sm font-bold leading-7 text-emerald-900" data-testid="post-job-gig-short-form-notice">
                This form only requires contact details and a job description. Title, location, pay, requirements, benefits, and deadline are optional.
              </div>
            )}
            <div className="grid gap-5 md:grid-cols-2">
              <TextField id="job_title" label={isGig ? "Gig title (optional)" : "Job title (optional)"} value={form.job_title} onChange={onChange} required={false} />
              <label className="grid gap-2" data-testid="category-field">
                <span className="text-xs font-bold uppercase tracking-[0.16em] text-[#475569]" data-testid="category-label">Category</span>
                <select name="category" value={form.category} onChange={onChange} className="min-h-11 border border-[#CBD5E1] bg-white px-3 text-sm text-[#0B1120] focus:outline-none focus:ring-2 focus:ring-[#0066FF]" data-testid="category-input">
                  {jobCategories.map((category) => <option key={category} value={category}>{category}</option>)}
                </select>
              </label>
              <TextField id="job_location" label={isGig ? "Gig location (optional)" : "Job location (optional)"} value={form.job_location} onChange={onChange} required={false} />
              <label className="grid gap-2" data-testid="job_type-field">
                <span className="text-xs font-bold uppercase tracking-[0.16em] text-[#475569]" data-testid="job_type-label">Job type</span>
                <select name="job_type" value={form.job_type} onChange={onChange} className="min-h-11 border border-[#CBD5E1] bg-white px-3 text-sm text-[#0B1120] focus:outline-none focus:ring-2 focus:ring-[#0066FF]" data-testid="job_type-input">
                  {jobTypes.map((type) => <option key={type} value={type}>{type}</option>)}
                </select>
              </label>
              <TextField id="pay_range" label={isGig ? "Pay range (optional)" : "Salary or pay range (optional)"} value={form.pay_range} onChange={onChange} required={false} />
              <TextField id="application_deadline" label="Application deadline" value={form.application_deadline} onChange={onChange} required={false} />
            </div>
            <TextField id="job_description" label="Job description" multiline value={form.job_description} onChange={onChange} />
            <TextField id="requirements" label="Requirements (optional)" multiline value={form.requirements} onChange={onChange} required={false} />
            <TextField id="benefits" label="Benefits" multiline value={form.benefits} onChange={onChange} required={false} />

            <div className="border-b border-[#E2E8F0] pb-2 pt-2">
              <h2 className="font-heading text-xl font-black text-[#0A2540]" data-testid="post-job-apply-section-title">Application method</h2>
            </div>
            {isHiringSupport ? (
              <div className="border-l-4 border-[#0066FF] bg-blue-50 p-4 text-sm font-semibold leading-7 text-[#0A2540]" data-testid="post-job-hiring-support-apply-notice">
                Candidates will apply through TalentBridge with contact details, resume, and a short message. TalentBridge reviews applications first and shares only shortlisted candidates with you.
              </div>
            ) : (
              <>
                <label className="grid gap-2" data-testid="apply_method-field">
                  <span className="text-xs font-bold uppercase tracking-[0.16em] text-[#475569]" data-testid="apply_method-label">How should candidates apply?</span>
                  <select name="apply_method" value={form.apply_method} onChange={onChange} className="min-h-11 border border-[#CBD5E1] bg-white px-3 text-sm text-[#0B1120] focus:outline-none focus:ring-2 focus:ring-[#0066FF]" data-testid="apply_method-input">
                    {applicationMethods.map((method) => <option key={method} value={method}>{method}</option>)}
                  </select>
                </label>
                {form.apply_method === "Email" && <TextField id="apply_email" label="Application email" type="email" value={form.apply_email} onChange={onChange} required={false} placeholder="Leave blank to use the business email above." />}
                {form.apply_method === "External link" && <TextField id="apply_url" label="Application link (optional)" value={form.apply_url} onChange={onChange} required={false} placeholder="https://company.ca/careers/job" />}
                {form.apply_method === "Instructions" && <TextField id="apply_instructions" label="Application instructions (optional)" multiline value={form.apply_instructions} onChange={onChange} required={false} placeholder="Tell candidates exactly what to send and where." />}
                <div className="grid gap-3 border border-[#D8E3F0] bg-white p-4 text-sm text-[#475569] md:grid-cols-3" data-testid="post-job-application-method-help">
                  <span className="inline-flex items-center gap-2" data-testid="post-job-application-email-help"><Mail className="h-4 w-4 text-[#0066FF]" /> Email applicants directly</span>
                  <span className="inline-flex items-center gap-2" data-testid="post-job-application-link-help"><ExternalLink className="h-4 w-4 text-[#0066FF]" /> Link to your ATS or website</span>
                  <span className="inline-flex items-center gap-2" data-testid="post-job-application-instructions-help"><ClipboardList className="h-4 w-4 text-[#0066FF]" /> Show custom instructions</span>
                </div>
              </>
            )}
            <TextField id="notes" label="Additional notes" multiline value={form.notes} onChange={onChange} required={false} />

            <button type="submit" disabled={loading} className="inline-flex items-center justify-center gap-2 bg-[#0066FF] px-6 py-4 font-bold text-white hover:bg-[#0052CC] disabled:opacity-60" data-testid="post-job-submit-button">
              {loading ? "Submitting..." : "Submit for approval"} {status === "success" ? <CheckCircle2 className="h-4 w-4" /> : <ArrowRight className="h-4 w-4" />}
            </button>
          </form>
        </div>
      </section>

      <section className="bg-[#F8FAFC] px-5 py-16 md:px-8 lg:py-20" data-testid="post-job-alerts-section">
        <div className="mx-auto max-w-5xl">
          <JobAlertsForm testId="post-job-alerts" />
        </div>
      </section>
    </PageShell>
  );
}